import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Form, Modal, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axiosConfig';
import './VehicleColors.css';

const VehicleColors = () => {
    const { vehicleId } = useParams();
    const navigate = useNavigate();

    const [vehicle, setVehicle] = useState(null);
    const [colors, setColors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingColor, setEditingColor] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        hex_code: '#000000',
        custom_name_en: '',
        custom_name_ar: '',
        image_url: '',
        sort_order: 0
    });
    const [errors, setErrors] = useState({});

    const validateHexCode = (value) => {
        const hexRegex = /^#[0-9A-Fa-f]{6}$/;
        return hexRegex.test(value);
    };

    const validateUrl = (value) => {
        if (!value) return true;
        try {
            new URL(value);
            return true;
        } catch {
            return false;
        }
    };

    const fetchVehicleData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/vehicles/${vehicleId}`);
            const vehicleData = response.data.data || response.data;
            setVehicle(vehicleData);
            setColors(vehicleData.colors || []);
        } catch (error) {
            console.error('Error fetching vehicle:', error);
            toast.error('Failed to load vehicle data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicleData();
    }, [vehicleId]);

    const handleOpenModal = (color = null) => {
        if (color) {
            setEditingColor(color);
            setFormData({
                name: color.name || '',
                hex_code: color.hex_code || '#000000',
                custom_name_en: color.custom_name_en || '',
                custom_name_ar: color.custom_name_ar || '',
                image_url: color.image_url || '',
                sort_order: color.sort_order || 0
            });
        } else {
            setEditingColor(null);
            setFormData({
                name: '',
                hex_code: '#000000',
                custom_name_en: '',
                custom_name_ar: '',
                image_url: '',
                sort_order: 0
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingColor(null);
        setErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Validate fields
        const newErrors = { ...errors };
        if (name === 'hex_code') {
            if (value && !validateHexCode(value)) {
                newErrors.hex_code = 'Please enter a valid hex code (e.g., #FF0000)';
            } else {
                delete newErrors.hex_code;
            }
        }
        if (name === 'image_url') {
            if (value && !validateUrl(value)) {
                newErrors.image_url = 'Please enter a valid URL';
            } else {
                delete newErrors.image_url;
            }
        }
        setErrors(newErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate before submit
        const newErrors = {};
        if (formData.hex_code && !validateHexCode(formData.hex_code)) {
            newErrors.hex_code = 'Please enter a valid hex code (e.g., #FF0000)';
        }
        if (formData.image_url && !validateUrl(formData.image_url)) {
            newErrors.image_url = 'Please enter a valid URL';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Please fix validation errors');
            return;
        }

        try {
            if (editingColor) {
                await axiosInstance.put(`/admin/vehicles/${vehicle.id}/colors/${editingColor.id}`, formData);
                toast.success('Color updated successfully!');
            } else {
                await axiosInstance.post(`/admin/vehicles/${vehicle.id}/colors`, formData);
                toast.success('Color added successfully!');
            }
            handleCloseModal();
            fetchVehicleData();
        } catch (error) {
            console.error('Error saving color:', error);
            toast.error(error.response?.data?.message || 'Failed to save color');
        }
    };

    const handleDelete = async (colorId) => {
        if (!window.confirm('Are you sure you want to delete this color?')) return;

        try {
            await axiosInstance.delete(`/admin/vehicles/${vehicle.id}/colors/${colorId}`);
            toast.success('Color deleted successfully!');
            fetchVehicleData();
        } catch (error) {
            console.error('Error deleting color:', error);
            toast.error('Failed to delete color');
        }
    };

    if (loading) {
        return <Container className="mt-5"><div className="text-center">Loading...</div></Container>;
    }

    return (
        <Container fluid className="mt-4">

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button variant="outline-secondary" onClick={() => navigate(`/vehicle-management/view/${vehicleId}`)} className="me-2">
                        Back to Vehicle
                    </Button>
                    <h3 className="d-inline">Manage Colors - {vehicle?.title_en}</h3>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    Add Color
                </Button>
            </div>

            <Card>
                <Card.Body>
                    <Table responsive striped bordered hover>
                        <thead className="brand-table-head">
                            <tr>
                                <th>Preview</th>
                                <th>Name</th>
                                <th>Hex Code</th>
                                <th>Custom Name (EN)</th>
                                <th>Custom Name (AR)</th>
                                <th>Sort Order</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {colors.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center">No colors found</td>
                                </tr>
                            ) : (
                                colors.map((color) => (
                                    <tr key={color.id}>
                                        <td>
                                            <div
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    backgroundColor: color.hex_code,
                                                    borderRadius: '50%',
                                                    border: '1px solid #ddd'
                                                }}
                                            />
                                        </td>
                                        <td>{color.name}</td>
                                        <td>{color.hex_code}</td>
                                        <td>{color.custom_name_en || '-'}</td>
                                        <td>{color.custom_name_ar || '-'}</td>
                                        <td>{color.sort_order}</td>
                                        <td>
                                            <Button variant="warning" size="sm" onClick={() => handleOpenModal(color)} className="me-2">
                                                Edit
                                            </Button>
                                            <Button variant="danger" size="sm" onClick={() => handleDelete(color.id)}>
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingColor ? 'Edit Color' : 'Add Color'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Color Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Hex Code</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="color"
                                    name="hex_code"
                                    value={formData.hex_code}
                                    onChange={handleInputChange}
                                    style={{ width: '60px', height: '40px' }}
                                />
                                <Form.Control
                                    type="text"
                                    name="hex_code"
                                    value={formData.hex_code}
                                    onChange={handleInputChange}
                                    pattern="^#[0-9A-Fa-f]{6}$"
                                    placeholder="#FF0000"
                                    isInvalid={!!errors.hex_code}
                                />
                            </div>
                            {errors.hex_code && (
                                <Form.Text className="text-danger">{errors.hex_code}</Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Custom Name (EN)</Form.Label>
                            <Form.Control
                                type="text"
                                name="custom_name_en"
                                value={formData.custom_name_en}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Custom Name (AR)</Form.Label>
                            <Form.Control
                                type="text"
                                name="custom_name_ar"
                                value={formData.custom_name_ar}
                                onChange={handleInputChange}
                                dir="rtl"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Image URL</Form.Label>
                            <Form.Control
                                type="text"
                                name="image_url"
                                value={formData.image_url}
                                onChange={handleInputChange}
                                placeholder="https://example.com/image.jpg"
                                isInvalid={!!errors.image_url}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.image_url}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Sort Order</Form.Label>
                            <Form.Control
                                type="number"
                                name="sort_order"
                                value={formData.sort_order}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="primary" type="submit">
                            {editingColor ? 'Update' : 'Add'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default VehicleColors;
